import { restClient } from '../client';
import {
  ActionItemUserContact, FormProgress, PatientFilledForm, ProviderClientScheduledPsychData,
  ProviderNewClient, ProviderNewPsychNote
} from '../resources';

export type ActionItemKey =
  | 'NEW_CALENDAR_WELCOME'
  | 'PROVIDER_UNSUBMITTED_SESSIONS'
  | 'PROVIDER_PATIENT_SCHEDULED_PSYCH'
  | 'PROVIDER_NEW_PSYCH_NOTE'
  | 'PROVIDER_NEW_CLIENTS'
  | 'PROVIDER_PATIENT_FILLED_ASSESSMENT'
  | 'CLIENT_SEE_PSYCHIATRIST'
  | 'CLIENT_FORM_PROGRESS'
  | 'CLIENT_OUTSTANDING_BALANCE'
  | 'CLIENT_BILLING'
  | 'CLIENT_EMERGENCY_CONTACT'
  | 'CLIENT_EMAIL_VERIFY'
  | 'CLIENT_SMS_VERIFY'
  | 'PROVIDER_INVITE_CLIENTS'
  | 'PROVIDER_GOOGLE_CALENDAR'
  | 'CLIENT_SEE_THERAPIST'
  | 'PROVIDER_SET_UP_2FA'
  | 'PROVIDER_VERIFY_ACCOUNT_EMAIL'
  | 'PROVIDER_SETUP_PAYOUT_ACCOUNT'
  | 'PROVIDER_UNSIGNED_COLLABORATIVE_AGREEMENTS'
  ;

export const enum ActionItemKeys {
  PROVIDER_WELCOME = 'PROVIDER_WELCOME',
  PROVIDER_CREDENTIALING = 'PROVIDER_CREDENTIALING',
  PROVIDER_TRAINING = 'PROVIDER_TRAINING',
  PROVIDER_MATCHING_PROFILE_SETUP = 'PROVIDER_MATCHING_PROFILE_SETUP',
  PROVIDER_MATCHING_AVAILABILITY = 'PROVIDER_MATCHING_AVAILABILITY',
  NEW_CALENDAR_WELCOME = 'NEW_CALENDAR_WELCOME',
  PROVIDER_UNSUBMITTED_SESSIONS = 'PROVIDER_UNSUBMITTED_SESSIONS',
  PROVIDER_PATIENT_SCHEDULED_PSYCH = 'PROVIDER_PATIENT_SCHEDULED_PSYCH',
  PROVIDER_NEW_PSYCH_NOTE = 'PROVIDER_NEW_PSYCH_NOTE',
  PROVIDER_NEW_CLIENTS = 'PROVIDER_NEW_CLIENTS',
  PROVIDER_PATIENT_FILLED_ASSESSMENT_HIGH_RISK = 'PROVIDER_PATIENT_FILLED_ASSESSMENT_HIGH_RISK',
  PROVIDER_PATIENT_FILLED_ASSESSMENT = 'PROVIDER_PATIENT_FILLED_ASSESSMENT',
  CLIENT_SEE_PSYCHIATRIST = 'CLIENT_SEE_PSYCHIATRIST',
  CLIENT_FORM_PROGRESS = 'CLIENT_FORM_PROGRESS',
  CLIENT_OUTSTANDING_BALANCE = 'CLIENT_OUTSTANDING_BALANCE',
  CLIENT_BILLING = 'CLIENT_BILLING',
  CLIENT_EMERGENCY_CONTACT = 'CLIENT_EMERGENCY_CONTACT',
  CLIENT_EMAIL_VERIFY = 'CLIENT_EMAIL_VERIFY',
  CLIENT_SMS_VERIFY = 'CLIENT_SMS_VERIFY',
  PROVIDER_INVITE_CLIENTS = 'PROVIDER_INVITE_CLIENTS',
  PROVIDER_GOOGLE_CALENDAR = 'PROVIDER_GOOGLE_CALENDAR',
  CLIENT_SEE_THERAPIST = 'CLIENT_SEE_THERAPIST',
  PROVIDER_SET_UP_2FA = 'PROVIDER_SET_UP_2FA',
  PROVIDER_VERIFY_ACCOUNT_EMAIL = 'PROVIDER_VERIFY_ACCOUNT_EMAIL',
  PROVIDER_SETUP_PAYOUT_ACCOUNT = 'PROVIDER_SETUP_PAYOUT_ACCOUNT',
  PROVIDER_UNSIGNED_COLLABORATIVE_AGREEMENTS = 'PROVIDER_UNSIGNED_COLLABORATIVE_AGREEMENTS',
}

export const enum NotificationResponse {
  later = 'later',
  dismiss = 'dismiss',
  initial = 'initial',
  email = 'email',
}

export const enum ActionItemStatus {
  todo = 'todo',
  inProgress = 'inProgress',
  inReview = 'inReview',
  approved = 'approved',
  done = 'done',
}

export type ActionItemStateByKey = {
  PROVIDER_SETUP_PAYOUT_ACCOUNT: {
    show: boolean;
    onboarding_url: string;
  };
  NEW_CALENDAR_WELCOME: {
    show_welcome_to_calendar_modal: boolean;
    video_url: string;
  };
  PROVIDER_UNSUBMITTED_SESSIONS: {
    unsubmitted_session_count: number;
  };
  PROVIDER_PATIENT_SCHEDULED_PSYCH: {
    clients: ProviderClientScheduledPsychData[];
  };
  PROVIDER_NEW_PSYCH_NOTE: {
    clients: ProviderNewPsychNote[];
  };
  PROVIDER_NEW_CLIENTS: {
    id: string;
    clients: ProviderNewClient[];
  };
  PROVIDER_PATIENT_FILLED_ASSESSMENT: {
    patients: PatientFilledForm[];
  };
  CLIENT_SEE_PSYCHIATRIST: {
    show: boolean;
    assessments: {
      phq9?: number;
      gad7?: number;
    };
    response: NotificationResponse;
    isInitial: boolean;
    displayPriority: number;
  };
  CLIENT_OUTSTANDING_BALANCE: {
    show: boolean;
    displayPriority: number;
    balance: number;
  };
  CLIENT_FORM_PROGRESS: {
    forms: Record<string, FormProgress>;
  };
  CLIENT_BILLING: {
    displayPriority: number;
    show: boolean;
    expired: boolean;
    expirationDays: number;
    declined: boolean;
    expMonth: number;
    expYear: number;
  };
  CLIENT_EMERGENCY_CONTACT: {
    displayPriority: number;
    name?: string;
    relationship?: string;
    phone?: string;
  };
  CLIENT_EMAIL_VERIFY: {
    show: boolean;
  };
  CLIENT_SMS_VERIFY: {
    show: boolean;
    contact?: ActionItemUserContact;
  };
  PROVIDER_INVITE_CLIENTS: {
    show: boolean;
  };
  PROVIDER_GOOGLE_CALENDAR: {
    show: boolean;
  };
  CLIENT_SEE_THERAPIST: {
    show: boolean;
  };
  PROVIDER_SET_UP_2FA: {
    show: boolean;
  };
  PROVIDER_VERIFY_ACCOUNT_EMAIL: {
    show: boolean;
  };
  PROVIDER_UNSIGNED_COLLABORATIVE_AGREEMENTS: {
    show: boolean;
    stateIds: string[];
  };
};

export const getActionItemState = <TKey extends ActionItemKey>(data: { key: TKey }) => {
  return restClient.request<{ data: ActionItemStateByKey[TKey]}>(`/api/v1/action_items/${data.key}`, { method: 'GET' });
};

export const getAllActionItemStates = () => {
  return restClient.request<{ data: ActionItemStateByKey }>('/api/v1/action_items/', { method: 'GET' });
};

type ActionItemResponse = {
  response: NotificationResponse;
  data?: Record<string, unknown>;
};

export type ActionItemResponseByKey = {
  NEW_CALENDAR_WELCOME: {
    response: NotificationResponse;
    data?: Record<string, unknown>;
  };
  PROVIDER_UNSUBMITTED_SESSIONS: ActionItemResponse;
  PROVIDER_PATIENT_SCHEDULED_PSYCH: {
    response: NotificationResponse;
    data?: {
      id: string;
    };
  };
  PROVIDER_NEW_PSYCH_NOTE: {
    response: NotificationResponse;
    data?: {
      id: string;
    };
  };
  PROVIDER_NEW_CLIENTS: {
    response: NotificationResponse;
    data?: {
      id: string;
    };
  };
  PROVIDER_PATIENT_FILLED_ASSESSMENT: ActionItemResponse;
  CLIENT_SEE_PSYCHIATRIST: {
    response: NotificationResponse;
  };
  CLIENT_FORM_PROGRESS: ActionItemResponse;
  CLIENT_OUTSTANDING_BALANCE: ActionItemResponse;
  CLIENT_BILLING: ActionItemResponse;
  CLIENT_EMERGENCY_CONTACT: ActionItemResponse;
  CLIENT_EMAIL_VERIFY: ActionItemResponse;
  CLIENT_SMS_VERIFY: ActionItemResponse;
  PROVIDER_INVITE_CLIENTS: ActionItemResponse;
  PROVIDER_GOOGLE_CALENDAR: ActionItemResponse;
  CLIENT_SEE_THERAPIST: ActionItemResponse;
  PROVIDER_SET_UP_2FA: ActionItemResponse;
  PROVIDER_VERIFY_ACCOUNT_EMAIL: ActionItemResponse;
  PROVIDER_SETUP_PAYOUT_ACCOUNT: ActionItemResponse;
  PROVIDER_UNSIGNED_COLLABORATIVE_AGREEMENTS: ActionItemResponse;
};

export const setActionItemResponse = <TKey extends ActionItemKey>(key: TKey, data: ActionItemResponseByKey[TKey]) =>
  restClient.request<{ data: ActionItemStateByKey[TKey] }>(`/api/v1/action_items/${key}/response`, {
    method: 'POST',
    data
  });
